import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { shopCategories, shopProducts } from '@/mocks/shop';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const shopSchema = {
  '@context': 'https://schema.org',
  '@type': 'Store',
  '@id': `${SITE_URL}/shop`,
  url: `${SITE_URL}/shop`,
  name: '学习用品商城 - 文具书籍儿童服装 | 金榜旺财',
  description: '金榜旺财学习用品商城，精选文具、教辅书籍、儿童服装、学习设备，一站式购齐，支持爱心捐助农村学生。',
  inLanguage: 'zh-CN',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: '学习用品商城', item: `${SITE_URL}/shop` },
    ],
  },
};

export default function ShopPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [cartIds, setCartIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const id = 'schema-shop';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(shopSchema);
    document.title = '学习用品商城 - 文具书籍儿童服装 | 金榜旺财';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '金榜旺财学习用品商城，精选文具、教辅书籍、儿童服装、学习设备，一站式购齐，支持爱心捐助农村学生。');
    return () => { const s = document.getElementById(id); if (s) s.remove(); };
  }, []);

  const filtered = shopProducts.filter((p) => {
    const matchCat = activeCategory === 'all' || p.category === activeCategory;
    const matchSearch = p.name.includes(searchQuery) || p.merchant.includes(searchQuery);
    return matchCat && matchSearch;
  });

  const toggleCart = (id: string) => {
    setCartIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <Navbar />

      {/* Header */}
      <div className="relative pt-20 pb-12 bg-gradient-to-br from-orange-600 to-red-700 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3">学习用品商城</h1>
          <p className="text-white/70 text-sm mb-8">精选文具、教辅书籍、儿童服装、学习设备，一站式购齐</p>
          <div className="max-w-lg mx-auto flex items-center bg-white rounded-full px-4 py-2.5 gap-3">
            <i className="ri-search-line text-gray-400 text-sm"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索商品、店铺..."
              className="flex-1 outline-none text-sm text-gray-700 bg-transparent"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Cart Badge */}
        {cartIds.size > 0 && (
          <div className="flex items-center justify-end mb-4">
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-4 py-2">
              <i className="ri-shopping-cart-line text-red-600"></i>
              <span className="text-red-600 text-sm font-semibold">购物车 ({cartIds.size})</span>
            </div>
          </div>
        )}

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {shopCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.id ? 'bg-red-600 text-white' : 'bg-white text-gray-600 hover:bg-red-50 hover:text-red-600'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((product) => (
            <Link key={product.id} to={`/shop/${product.id}`} className="bg-white rounded-2xl overflow-hidden group cursor-pointer hover:scale-[1.02] transition-all block" data-product-shop>
              <div className="relative overflow-hidden h-52">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 bg-red-600 text-white text-xs px-2.5 py-1 rounded-full font-medium">
                  -{Math.round((1 - product.price / product.originalPrice) * 100)}%
                </div>
              </div>
              <div className="p-4">
                <p className="text-xs text-gray-400 mb-1">{product.merchant}</p>
                <h3 className="font-bold text-gray-900 text-sm leading-snug mb-2 line-clamp-2">{product.name}</h3>
                <div className="flex items-center gap-1 mb-3">
                  <i className="ri-star-fill text-amber-400 text-xs"></i>
                  <span className="text-xs font-semibold text-gray-700">{product.rating}</span>
                  <span className="text-xs text-gray-400">({product.sales.toLocaleString()}件已售)</span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-red-600 font-bold text-base">¥{product.price}</span>
                    <span className="text-gray-300 text-xs line-through ml-1">¥{product.originalPrice}</span>
                  </div>
                  <button
                    onClick={(e) => { e.preventDefault(); toggleCart(product.id); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                      cartIds.has(product.id) ? 'bg-green-100 text-green-700' : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  >
                    <i className={cartIds.has(product.id) ? 'ri-check-line' : 'ri-shopping-cart-line'}></i>
                    {cartIds.has(product.id) ? '已加入' : '加入购物车'}
                  </button>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20">
            <i className="ri-store-2-line text-5xl text-gray-300 mb-4 block"></i>
            <p className="text-gray-400">暂无相关商品</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
